import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import Swal from 'sweetalert2';
import axios from 'axios';
import { useGenerateDispatch } from "../context/GenerateContext";
import { usePromptusAPIState } from "../context/PromptusAPIContext";
import { useAccountContext } from "../context/AccountContext";
import { useInstallContext } from "../context/InstallContext";
import { generatePrompt, getApiBaseUrl, getProgress, getGenerateOutput, getComfyOutput, getUseDepApi, getAuthorizationToken } from "../api-helper";
import { getServerForModel, checkOfflineServersAvailable, getOnlineAvailableModels, getLocalAvailableModels } from '../services/offlineModelService';
import { CosyflowStatusBar } from '../components/CosyflowPicker/CosyflowStatusBar';
import { useCosyflowHandlers } from '../hooks/useCosyflowHandlers';
import electronBridge from '../utils/electronBridge';
import { isStaff, isStaffOrCreator } from '../utils/accountTypes';
import { db } from '../db';
import { getAudioMimeFromBase64 } from '../utils/audioUtils';
import { getMediaTypeFromUrl, getMediaType } from '../utils/MediaOperations';
import { saveGeneratedMedia } from '../services/MediaStorageService';
import Video from '../components/Video.jsx';
import Audio from '../components/Audio.jsx';
import ModelCanvas from '../components/3DCanvas.jsx';
import DynamicPromptForm from '../components/DynamicPromptForm.jsx';
import { computeVariableCostProduct } from '../utils/costUtils';
import { SiYoutube } from 'react-icons/si';
import { getThumbnailUrl } from '../services/youtubeService';
import YouTubeVideoPlayer from '../components/YouTubeVideoPlayer';
import LoginModal from '../components/LoginModal';

// Base cost added to all generations (matches main PWA and templates app)
const BASE_COST = 6;

// Generate a consistent placeholder based on template ID
const getPlaceholderImage = (templateId) => {
    return `https://picsum.photos/seed/${templateId}/800/600`;
};

// Loading Spinner Component
const PageLoading = ({ text = "Loading..." }) => (
    <div className="min-h-screen flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
            <div className="w-12 h-12 border-4 border-white/20 border-t-yellow rounded-full animate-spin" />
            <p className="text-white/60">{text}</p>
        </div>
    </div>
);

const ButtonSpinner = () => (
    <div className="w-5 h-5 border-2 border-current border-t-transparent rounded-full animate-spin" />
);

// Output type icons and colors
const outputTypeConfig = {
    image: {
        icon: (
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
        ),
        color: 'bg-blue-500/20 text-blue-400',
        label: 'Image',
    },
    video: {
        icon: (
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
            </svg>
        ),
        color: 'bg-purple-500/20 text-purple-400',
        label: 'Video',
    },
    audio: {
        icon: (
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zm12-3c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zM9 10l12-3" />
            </svg>
        ),
        color: 'bg-green-500/20 text-green-400',
        label: 'Audio',
    },
    '3d': {
        icon: (
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 10l-2 1m0 0l-2-1m2 1v2.5M20 7l-2 1m2-1l-2-1m2 1v2.5M14 4l-2-1-2 1M4 7l2-1M4 7l2 1M4 7v2.5M12 21l-2-1m2 1l2-1m-2 1v-2.5M6 18l-2-1v-2.5M18 18l2-1v-2.5" />
            </svg>
        ),
        color: 'bg-orange-500/20 text-orange-400',
        label: '3D',
    },
};

// Media type background colors
const mediaBackgroundColors = {
    video: 'bg-gradient-to-br from-purple-900/80 to-purple-800/60',
    audio: 'bg-gradient-to-br from-green-900/80 to-green-800/60',
    '3d': 'bg-gradient-to-br from-orange-900/80 to-orange-800/60',
};

// Helper function to detect output type from category
const detectOutputType = (category) => {
    if (!category) return 'image';
    const lower = category.toLowerCase();
    if (lower.includes('video') || lower.includes('animation')) return 'video';
    if (lower.includes('audio') || lower.includes('music') || lower.includes('sound')) return 'audio';
    if (lower.includes('3d') || lower.includes('mesh') || lower.includes('model')) return '3d';
    return 'image';
};

// Generate thumbnail from video source
const generateVideoThumbnail = (videoSrc, maxSize = 300, quality = 0.85) => {
    return new Promise((resolve) => {
        const video = document.createElement('video');
        video.crossOrigin = 'anonymous';
        video.muted = true;
        video.playsInline = true;

        video.onloadeddata = () => {
            video.currentTime = Math.min(1, video.duration / 2);
        };

        video.onseeked = () => {
            try {
                const canvas = document.createElement('canvas');
                const aspectRatio = video.videoWidth / video.videoHeight;

                if (aspectRatio > 1) {
                    canvas.width = maxSize;
                    canvas.height = Math.round(maxSize / aspectRatio);
                } else {
                    canvas.height = maxSize;
                    canvas.width = Math.round(maxSize * aspectRatio);
                }

                const ctx = canvas.getContext('2d');
                ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

                const thumbnail = canvas.toDataURL('image/jpeg', quality);
                video.src = '';
                resolve(thumbnail);
            } catch (err) {
                console.warn('Failed to generate video thumbnail:', err);
                resolve(null);
            }
        };

        video.onerror = () => {
            console.warn('Failed to load video for thumbnail');
            resolve(null);
        };

        setTimeout(() => resolve(null), 5000);

        video.src = videoSrc;
        video.load();
    });
};

// Media type icons for thumbnails
const MediaTypeIcon = ({ type, className = "w-6 h-6" }) => {
    switch (type) {
        case 'video':
            return (
                <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                </svg>
            );
        case 'audio':
            return (
                <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zm12-3c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zM9 10l12-3" />
                </svg>
            );
        case '3d':
            return (
                <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 10l-2 1m0 0l-2-1m2 1v2.5M20 7l-2 1m2-1l-2-1m2 1v2.5M14 4l-2-1-2 1M4 7l2-1M4 7l2 1M4 7v2.5M12 21l-2-1m2 1l2-1m-2 1v-2.5M6 18l-2-1v-2.5M18 18l2-1v-2.5" />
                </svg>
            );
        default:
            return (
                <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
            );
    }
};

// Media Slider Component with Fullscreen Support for all media types
const MediaSlider = ({ images, alt }) => {
    const [currentIndex, setCurrentIndex] = useState(0);
    const [isFullscreen, setIsFullscreen] = useState(false);
    const [mediaError, setMediaError] = useState({});

    const mediaItems = images.map(url => ({
        url,
        type: getMediaTypeFromUrl(url)
    }));

    const goToPrevious = useCallback(() => {
        setCurrentIndex((prev) => (prev === 0 ? mediaItems.length - 1 : prev - 1));
    }, [mediaItems.length]);

    const goToNext = useCallback(() => {
        setCurrentIndex((prev) => (prev === mediaItems.length - 1 ? 0 : prev + 1));
    }, [mediaItems.length]);

    const handleKeyDown = useCallback((e) => {
        if (e.key === 'ArrowLeft') {
            goToPrevious();
        } else if (e.key === 'ArrowRight') {
            goToNext();
        } else if (e.key === 'Escape' && isFullscreen) {
            setIsFullscreen(false);
        }
    }, [goToPrevious, goToNext, isFullscreen]);

    const handleMediaError = useCallback((index) => {
        setMediaError((prev) => ({ ...prev, [index]: true }));
    }, []);

    const renderMedia = (mediaItem, index, isFullscreenView = false) => {
        const { url, type } = mediaItem;

        if (mediaError[index]) {
            return (
                <div className="w-full h-full flex items-center justify-center bg-dark-blue/50">
                    <span className="text-white/50">Failed to load media</span>
                </div>
            );
        }

        switch (type) {
            case 'video':
                return (
                    <div
                        className={isFullscreenView ? "max-w-full max-h-full" : "flex items-center justify-center"}
                        onMouseEnter={(e) => {
                            const video = e.currentTarget.querySelector('video');
                            if (video) video.pause();
                        }}
                        onMouseLeave={(e) => {
                            const video = e.currentTarget.querySelector('video');
                            if (video) video.play().catch(() => {});
                        }}
                    >
                        <video
                            src={url}
                            controls
                            autoPlay
                            muted
                            loop
                            playsInline
                            className={isFullscreenView ? "max-w-full max-h-full object-contain" : "max-w-full max-h-[45vh] object-contain"}
                            onError={() => handleMediaError(index)}
                        />
                    </div>
                );
            case 'audio':
                return (
                    <div className={`flex flex-col items-center justify-center bg-gradient-to-br from-green-900/80 to-green-800/60 ${isFullscreenView ? 'w-full max-w-2xl p-8' : 'w-full h-full p-4'}`}>
                        <MediaTypeIcon type="audio" className={isFullscreenView ? "w-24 h-24 text-green-400 mb-6" : "w-16 h-16 text-green-400 mb-4"} />
                        <audio
                            src={url}
                            controls
                            className="w-full"
                            onError={() => handleMediaError(index)}
                        />
                    </div>
                );
            case '3d':
                return (
                    <div className={`flex flex-col items-center justify-center bg-gradient-to-br from-orange-900/80 to-orange-800/60 ${isFullscreenView ? 'w-full h-full' : 'w-full h-full'}`}>
                        <ModelCanvas
                            url={url}
                            image={url}
                        />
                    </div>
                );
            default:
                return (
                    <img
                        src={url}
                        alt={`${alt} - ${index + 1}`}
                        className={isFullscreenView ? "max-w-full max-h-full object-contain" : "max-w-full max-h-[45vh] object-contain cursor-pointer transition-opacity duration-300"}
                        onClick={!isFullscreenView ? () => setIsFullscreen(true) : undefined}
                        onError={() => handleMediaError(index)}
                    />
                );
        }
    };

    const renderThumbnail = (mediaItem, index) => {
        const { url, type } = mediaItem;

        if (type === 'image' || type === 'Unknown') {
            return (
                <img
                    src={url}
                    alt={`${alt} thumbnail ${index + 1}`}
                    className="w-full h-full object-cover"
                    onError={() => handleMediaError(index)}
                />
            );
        }

        const bgColors = {
            video: 'bg-purple-900/80',
            audio: 'bg-green-900/80',
            '3d': 'bg-orange-900/80'
        };

        return (
            <div className={`w-full h-full flex items-center justify-center ${bgColors[type] || 'bg-dark-blue/80'}`}>
                <MediaTypeIcon type={type} className="w-6 h-6 text-white" />
            </div>
        );
    };

    if (!images || images.length === 0) return null;

    const currentMedia = mediaItems[currentIndex];

    // Single media item - simpler display with fullscreen
    if (mediaItems.length === 1) {
        return (
            <>
                <div className="relative rounded-xl overflow-hidden bg-dark-blue/50 min-h-[200px] max-h-[45vh] flex items-center justify-center">
                    {renderMedia(currentMedia, 0)}
                    {currentMedia.type === 'image' && (
                        <button
                            onClick={() => setIsFullscreen(true)}
                            className="absolute top-3 right-3 p-2 rounded-full bg-black/50 text-white hover:bg-black/70 transition-colors"
                            aria-label="View fullscreen"
                        >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
                            </svg>
                        </button>
                    )}
                    {currentMedia.type !== 'image' && currentMedia.type !== 'Unknown' && (
                        <div className="absolute top-3 left-3 px-2 py-1 rounded-md bg-black/50 text-white text-xs flex items-center gap-1">
                            <MediaTypeIcon type={currentMedia.type} className="w-4 h-4" />
                            <span className="capitalize">{currentMedia.type}</span>
                        </div>
                    )}
                </div>

                {/* Fullscreen Modal - Single Item */}
                {isFullscreen && (
                    <div
                        className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center"
                        onClick={() => setIsFullscreen(false)}
                        onKeyDown={handleKeyDown}
                        tabIndex={0}
                    >
                        <button
                            onClick={() => setIsFullscreen(false)}
                            className="absolute top-4 right-4 p-3 rounded-full bg-white/10 text-white hover:bg-white/20 transition-colors z-10"
                            aria-label="Close fullscreen"
                        >
                            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>
                        <div className="flex items-center justify-center w-full h-full p-16" onClick={(e) => e.stopPropagation()}>
                            {renderMedia(currentMedia, 0, true)}
                        </div>
                    </div>
                )}
            </>
        );
    }

    return (
        <>
            <div
                className="relative"
                tabIndex={0}
                onKeyDown={handleKeyDown}
            >
                {/* Main Media Display */}
                <div className="relative rounded-xl overflow-hidden bg-dark-blue/50 min-h-[200px] max-h-[45vh] flex items-center justify-center">
                    {renderMedia(currentMedia, currentIndex)}

                    {/* Navigation Arrows */}
                    <button
                        onClick={goToPrevious}
                        className="absolute left-3 top-1/2 -translate-y-1/2 w-10 h-10 bg-black/50 hover:bg-black/70 rounded-full flex items-center justify-center transition-colors z-10"
                    >
                        <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                        </svg>
                    </button>
                    <button
                        onClick={goToNext}
                        className="absolute right-3 top-1/2 -translate-y-1/2 w-10 h-10 bg-black/50 hover:bg-black/70 rounded-full flex items-center justify-center transition-colors z-10"
                    >
                        <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                    </button>

                    {/* Media Counter */}
                    <div className="absolute bottom-3 right-3 px-3 py-1.5 rounded-md bg-black/50 text-white text-sm z-10">
                        {currentIndex + 1} / {mediaItems.length}
                    </div>

                    {/* Media Type Badge */}
                    {currentMedia.type !== 'image' && currentMedia.type !== 'Unknown' && (
                        <div className="absolute top-3 left-3 px-2 py-1 rounded-md bg-black/50 text-white text-xs flex items-center gap-1 z-10">
                            <MediaTypeIcon type={currentMedia.type} className="w-4 h-4" />
                            <span className="capitalize">{currentMedia.type}</span>
                        </div>
                    )}

                    {/* Fullscreen Button (only for images) */}
                    {(currentMedia.type === 'image' || currentMedia.type === 'Unknown') && (
                        <button
                            onClick={() => setIsFullscreen(true)}
                            className="absolute top-3 right-3 p-2 rounded-full bg-black/50 text-white hover:bg-black/70 transition-colors z-10"
                            aria-label="View fullscreen"
                        >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
                            </svg>
                        </button>
                    )}
                </div>

                {/* Thumbnail Strip */}
                <div className="mt-3 flex gap-2 overflow-x-auto pb-2 scrollbar-thin scrollbar-thumb-white/20">
                    {mediaItems.map((mediaItem, index) => (
                        <button
                            key={index}
                            onClick={() => setCurrentIndex(index)}
                            className={`relative flex-shrink-0 w-16 h-16 rounded-lg overflow-hidden transition-all ${
                                index === currentIndex
                                    ? 'ring-2 ring-yellow ring-offset-2 ring-offset-dark-blue'
                                    : 'opacity-60 hover:opacity-100'
                            }`}
                        >
                            {renderThumbnail(mediaItem, index)}
                        </button>
                    ))}
                </div>
            </div>

            {/* Fullscreen Modal */}
            {isFullscreen && (
                <div
                    className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center"
                    onClick={() => setIsFullscreen(false)}
                    onKeyDown={handleKeyDown}
                    tabIndex={0}
                >
                    {/* Close Button */}
                    <button
                        onClick={() => setIsFullscreen(false)}
                        className="absolute top-4 right-4 p-3 rounded-full bg-white/10 text-white hover:bg-white/20 transition-colors z-10"
                        aria-label="Close fullscreen"
                    >
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>

                    {/* Main Media - fullscreen display */}
                    <div
                        className="flex items-center justify-center w-full h-full p-16"
                        onClick={(e) => e.stopPropagation()}
                    >
                        {renderMedia(currentMedia, currentIndex, true)}
                    </div>

                    {/* Navigation Arrows - Fullscreen */}
                    <button
                        onClick={(e) => { e.stopPropagation(); goToPrevious(); }}
                        className="absolute left-4 top-1/2 -translate-y-1/2 p-3 rounded-full bg-white/10 text-white hover:bg-white/20 transition-colors"
                        aria-label="Previous media"
                    >
                        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                        </svg>
                    </button>
                    <button
                        onClick={(e) => { e.stopPropagation(); goToNext(); }}
                        className="absolute right-4 top-1/2 -translate-y-1/2 p-3 rounded-full bg-white/10 text-white hover:bg-white/20 transition-colors"
                        aria-label="Next media"
                    >
                        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                    </button>

                    {/* Media Counter - Fullscreen */}
                    <div className="absolute bottom-4 left-1/2 -translate-x-1/2 px-4 py-2 rounded-full bg-white/10 text-white text-sm flex items-center gap-2">
                        {currentMedia.type !== 'image' && currentMedia.type !== 'Unknown' && (
                            <MediaTypeIcon type={currentMedia.type} className="w-4 h-4" />
                        )}
                        <span>{currentIndex + 1} / {mediaItems.length}</span>
                    </div>

                    {/* Thumbnail Strip - Fullscreen */}
                    <div className="absolute bottom-16 left-1/2 -translate-x-1/2 flex gap-2 p-2 bg-black/50 rounded-lg max-w-[80vw] overflow-x-auto">
                        {mediaItems.map((mediaItem, index) => (
                            <button
                                key={index}
                                onClick={(e) => { e.stopPropagation(); setCurrentIndex(index); }}
                                className={`relative flex-shrink-0 w-12 h-12 rounded-md overflow-hidden transition-all ${
                                    index === currentIndex
                                        ? 'ring-2 ring-yellow'
                                        : 'opacity-50 hover:opacity-100'
                                }`}
                            >
                                {renderThumbnail(mediaItem, index)}
                            </button>
                        ))}
                    </div>
                </div>
            )}
        </>
    );
};

// Generated Media Modal
const GeneratedMediaModal = ({ media, isOpen, onClose }) => {
    if (!isOpen || !media) return null;

    const isUrl = (str) => {
        if (!str) return false;
        const trimmed = str.trim().toLowerCase();
        return trimmed.startsWith('http://') || trimmed.startsWith('https://') || trimmed.startsWith('data:');
    };

    const mediaType = media.type || 'image';
    const getBase64Prefix = () => {
        switch (mediaType) {
            case 'video': return 'data:video/mp4;base64,';
            case 'audio': return `data:${getAudioMimeFromBase64(media.image)};base64,`;
            default: return 'data:image/webp;base64,';
        }
    };

    const mediaUrl = isUrl(media.image)
        ? media.image
        : `${getBase64Prefix()}${media.image}`;

    const getDownloadFilename = () => {
        switch (mediaType) {
            case 'video': return 'generated-video.mp4';
            case 'audio': return 'generated-audio.mp3';
            case '3d': return 'generated-model.glb';
            default: return 'generated-image.webp';
        }
    };

    const renderMedia = () => {
        switch (mediaType) {
            case 'video':
                return (
                    <video
                        src={mediaUrl}
                        controls
                        autoPlay
                        className="max-w-full max-h-[60vh] rounded-lg mx-auto"
                    />
                );
            case 'audio':
                return (
                    <div className="flex flex-col items-center gap-4 py-8">
                        <div className="w-32 h-32 rounded-full bg-green-500/20 flex items-center justify-center">
                            <svg className="w-16 h-16 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2z" />
                            </svg>
                        </div>
                        <audio
                            src={mediaUrl}
                            controls
                            autoPlay
                            className="w-full max-w-md"
                        />
                    </div>
                );
            case '3d':
                return (
                    <div className="flex flex-col items-center gap-4 py-8">
                        <div className="w-32 h-32 rounded-full bg-orange-500/20 flex items-center justify-center">
                            <svg className="w-16 h-16 text-orange-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 10l-2 1m0 0l-2-1m2 1v2.5M20 7l-2 1m2-1l-2-1m2 1v2.5M14 4l-2-1-2 1M4 7l2-1M4 7l2 1M4 7v2.5M12 21l-2-1m2 1l2-1m-2 1v-2.5M6 18l-2-1v-2.5M18 18l2-1v-2.5" />
                            </svg>
                        </div>
                        <p className="text-white/70">3D Model Generated</p>
                        <p className="text-white/50 text-sm">Download to view in a 3D viewer</p>
                    </div>
                );
            default:
                return (
                    <img
                        src={mediaUrl}
                        alt="Generated"
                        className="max-w-full h-auto rounded-lg mx-auto"
                    />
                );
        }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80" onClick={onClose}>
            <div
                className="bg-dark-blue border-2 border-cyan/50 rounded-xl p-6 max-w-4xl w-[90%] max-h-[90vh] overflow-auto"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="flex items-center justify-between mb-4">
                    <h2 className="text-xl font-bold text-white">Generated Content</h2>
                    <button
                        onClick={onClose}
                        className="text-white/60 hover:text-white transition-colors"
                    >
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>
                {renderMedia()}
                <div className="flex gap-3 justify-center mt-4">
                    <button
                        onClick={onClose}
                        className="bg-dark-blue/50 hover:bg-dark-blue/70 text-white font-bold py-2 px-4 rounded-lg transition-all border border-white/20"
                    >
                        Close
                    </button>
                    <a
                        href={mediaUrl}
                        download={getDownloadFilename()}
                        className="bg-yellow hover:bg-yellow/90 text-dark-blue font-bold py-2 px-4 rounded-lg transition-all"
                    >
                        Download
                    </a>
                </div>
            </div>
        </div>
    );
};

export default function TemplateDetail() {
    const { templateId } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const generateStatusDispatch = useGenerateDispatch();
    const apiState = usePromptusAPIState();
    const accountContext = useAccountContext();
    const { startInstall, isInstalling: checkIsInstalling } = useInstallContext();

    const {
        cosyflowData: passedCosyflowData,
        availableModels: passedAvailableModels,
        localAvailableModels: passedLocalAvailableModels
    } = location.state || {};

    const cosyflowHandlers = useCosyflowHandlers({
        navigate,
        accountNo: accountContext.accountDetails?.accountNo,
        accountAlias: accountContext.accountDetails?.alias,
        startInstall,
        updateCosyflow: null,
        onOpenEditModal: null,
        onOpenWizard: (cosyflow) => {
            // Navigate to Cosyflows page with state to open wizard
            navigate('/cosyflows', { state: { openWizard: true, wizardCosyflow: cosyflow } });
        },
        onRefresh: () => {},
    });

    const [template, setTemplate] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [notFound, setNotFound] = useState(false);
    const [userPrompt, setUserPrompt] = useState('');
    const [isLoadingDefaults, setIsLoadingDefaults] = useState(false);
    const [modelDefaults, setModelDefaults] = useState(null);
    const [requiredFields, setRequiredFields] = useState([]);
    const [fieldValues, setFieldValues] = useState({});
    const [isGenerating, setIsGenerating] = useState(false);
    const [generationProgress, setGenerationProgress] = useState(0);
    const [generationStatus, setGenerationStatus] = useState('');
    const [generatedImage, setGeneratedImage] = useState(null);
    const [showGeneratedModal, setShowGeneratedModal] = useState(false);
    const [sessionGenerations, setSessionGenerations] = useState([]);
    const [isPromptExpanded, setIsPromptExpanded] = useState(false);
    const [categoryTariffs, setCategoryTariffs] = useState(new Map());
    const [shareUrl, setShareUrl] = useState('');
    const [modelAvailability, setModelAvailability] = useState({ online: false, offline: false, available: false, checked: false });
    const [fetchedOnlineModels, setFetchedOnlineModels] = useState([]);
    const [fetchedLocalModels, setFetchedLocalModels] = useState([]);
    const fileInputRefs = useRef({});
    const dynamicFormRef = useRef(null);
    const [tutorialVideos, setTutorialVideos] = useState([]);
    const [showTutorialDropdown, setShowTutorialDropdown] = useState(false);
    const [selectedTutorialVideo, setSelectedTutorialVideo] = useState(null);
    const [showLoginModal, setShowLoginModal] = useState(false);
    const tutorialDropdownRef = useRef(null);

    // Character limit for collapsed prompt
    const PROMPT_CHAR_LIMIT = 150;

    // Initialize share URL on mount
    useEffect(() => {
        const initShareUrl = async () => {
            const path = `/cosyflows/${templateId}`;
            if (electronBridge?.isElectron) {
                const isDevApi = await getUseDepApi();
                const baseUrl = isDevApi ? 'https://login.dev.promptus.ai/pwa_demo/#' : 'https://login.promptus.ai/pwa_demo/#';
                setShareUrl(`${baseUrl}${path}`);
            } else {
                if (window.location.origin.includes('localhost')) {
                    setShareUrl(`${window.location.origin}/#${path}`);
                } else {
                    setShareUrl(`${window.location.origin}/pwa_demo/#${path}`);
                }
            }
        };
        initShareUrl();
    }, [templateId]);

    // Fetch YouTube tutorials for this cosyflow
    useEffect(() => {
        let cancelled = false;
        const fetchTutorials = async () => {
            try {
                const apiBase = await getApiBaseUrl();
                const url = `${apiBase}/api/youtube/tutorials/cosyflow/${templateId}`;
                const appToken = process.env.REACT_APP_API_TOKEN;
                const sessionToken = getAuthorizationToken();

                // Try session token first, fall back to app token if 403
                let response = await fetch(url, {
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${sessionToken}`,
                    },
                    signal: AbortSignal.timeout(10000),
                });

                if (response.status === 403 && appToken && appToken !== sessionToken) {
                    response = await fetch(url, {
                        headers: {
                            'Content-Type': 'application/json',
                            'Authorization': `Bearer ${appToken}`,
                        },
                        signal: AbortSignal.timeout(10000),
                    });
                }

                if (!response.ok) return;
                const data = await response.json();
                const rawVideos = data.tutorials || data.videos || data;
                if (!cancelled && Array.isArray(rawVideos) && rawVideos.length > 0) {
                    setTutorialVideos(rawVideos.map(v => ({
                        id: v.youtubeVideoId || v.id || v.videoId,
                        title: v.title || '',
                        description: v.description || '',
                    })));
                }
            } catch (e) {
                console.error('[TemplateDetail] Tutorial fetch failed:', e.message);
            }
        };
        fetchTutorials();
        return () => { cancelled = true; };
    }, [templateId]);

    // Close tutorial dropdown on outside click
    useEffect(() => {
        if (!showTutorialDropdown) return;
        const handleClick = (e) => {
            if (tutorialDropdownRef.current && !tutorialDropdownRef.current.contains(e.target)) {
                setShowTutorialDropdown(false);
            }
        };
        document.addEventListener('mousedown', handleClick);
        return () => document.removeEventListener('mousedown', handleClick);
    }, [showTutorialDropdown]);

    // Fetch category tariffs on mount
    useEffect(() => {
        const fetchTariffs = async () => {
            try {
                const apiBase = await getApiBaseUrl();
                const response = await axios.get(`${apiBase}/api/cosyflows/categories`, {
                    headers: {
                        "Content-Type": "application/json",
                        "Authorization": `Bearer ${process.env.REACT_APP_API_TOKEN}`
                    }
                });

                if (response.data?.categories && Array.isArray(response.data.categories)) {
                    const tariffMap = new Map();
                    response.data.categories.forEach((item) => {
                        if (item.cosyflow_category && item.cost !== undefined) {
                            const cost = typeof item.cost === 'string' ? parseFloat(item.cost) : item.cost;
                            if (!isNaN(cost)) {
                                tariffMap.set(item.cosyflow_category, cost);
                            }
                        }
                    });
                    setCategoryTariffs(tariffMap);
                }
            } catch (error) {
                console.error('[TemplateDetail] Error fetching category tariffs:', error);
            }
        };

        fetchTariffs();
    }, []);

    // Get display cost for a category (with BASE_COST added)
    const getCostDisplay = useCallback((category) => {
        if (!category || categoryTariffs.size === 0) return null;

        if (categoryTariffs.has(category)) {
            const cost = categoryTariffs.get(category) + BASE_COST;
            return cost >= 1 ? Math.round(cost).toString() : cost.toString();
        }

        // Try fuzzy matching
        const categoryParts = category.split('/');
        const mediaType = categoryParts[categoryParts.length - 1];
        const mainType = categoryParts[0];

        for (const [tariffCategory, cost] of categoryTariffs.entries()) {
            if (tariffCategory.endsWith('/' + mediaType) ||
                tariffCategory.startsWith(mainType + '/') ||
                tariffCategory === mainType ||
                tariffCategory.toLowerCase().includes(mediaType.toLowerCase())) {
                const displayCost = cost + BASE_COST;
                return displayCost >= 1 ? Math.round(displayCost).toString() : displayCost.toString();
            }
        }

        return null;
    }, [categoryTariffs]);

    // Fetch template on mount
    useEffect(() => {
        const fetchData = async () => {
            try {
                let transformedTemplate = null;

                // If cosyflow data was passed via navigation state, use it directly (instant load)
                if (passedCosyflowData) {
                    console.log('[TemplateDetail] Using passed cosyflow data:', passedCosyflowData);

                    let cosyflowData = null;
                    let basePrompt = '';

                    if (passedCosyflowData.cosyflow) {
                        try {
                            cosyflowData = typeof passedCosyflowData.cosyflow === 'string'
                                ? JSON.parse(passedCosyflowData.cosyflow)
                                : passedCosyflowData.cosyflow;

                            if (cosyflowData?.variables) {
                                for (const [key, fieldDef] of Object.entries(cosyflowData.variables)) {
                                    if (fieldDef.type === 'prompt' && fieldDef.default) {
                                        basePrompt = fieldDef.default;
                                        break;
                                    }
                                }
                            }
                        } catch (e) {
                            console.error('[TemplateDetail] Error parsing passed cosyflow JSON:', e);
                        }
                    }

                    let exampleImages = passedCosyflowData.exampleImages || [];
                    if (!exampleImages.length && cosyflowData?.metadata?.example_urls) {
                        exampleImages = cosyflowData.metadata.example_urls;
                    } else if (!exampleImages.length && cosyflowData?.metadata?.preview_images) {
                        exampleImages = cosyflowData.metadata.preview_images;
                    }

                    transformedTemplate = {
                        id: passedCosyflowData.uid || passedCosyflowData.id,
                        uid: passedCosyflowData.uid || passedCosyflowData.id,
                        title: passedCosyflowData.title,
                        description: passedCosyflowData.description || '',
                        model: passedCosyflowData.title,
                        prompt: passedCosyflowData.prompt || '',
                        basePrompt: basePrompt || passedCosyflowData.prompt || '',
                        category: passedCosyflowData.category || 'General',
                        imageUrl: passedCosyflowData.imageUrl || '',
                        credits_cost: passedCosyflowData.credits_cost,
                        outputType: passedCosyflowData.outputType || detectOutputType(passedCosyflowData.category),
                        creator: passedCosyflowData.creator,
                        accountNo: passedCosyflowData.accountNo,
                        trusted: passedCosyflowData.trusted,
                        isPublic: passedCosyflowData.isPublic,
                        distributed: passedCosyflowData.distributed,
                        exampleImages: exampleImages,
                        cosyflowData: cosyflowData,
                        creditsPerUnit: passedCosyflowData.creditsPerUnit || null,
                    };

                    // If creditsPerUnit not in passed data, try db.cosyflows meta
                    if (!transformedTemplate.creditsPerUnit) {
                        try {
                            const dbRecord = await db.cosyflows.where('data.title').equals(passedCosyflowData.title).first();
                            if (dbRecord?.meta?.creditsPerUnit) {
                                transformedTemplate.creditsPerUnit = dbRecord.meta.creditsPerUnit;
                            }
                        } catch (e) { /* non-critical */ }
                    }

                    setTemplate(transformedTemplate);
                    setIsLoading(false);
                    return;
                }

                // Otherwise, fetch from API (for direct URL access)
                const apiBase = await getApiBaseUrl();

                console.log('[TemplateDetail] Fetching as cosyflow:', templateId);
                const response = await axios.get(`${apiBase}/api/cosyflows/query?uid=${templateId}&trusted=`, {
                    headers: {
                        "Content-Type": "application/json",
                        "Authorization": `Bearer ${process.env.REACT_APP_API_TOKEN}`
                    }
                });

                if (response.data?.cosyflows && response.data.cosyflows.length > 0) {
                    const cosyflow = response.data.cosyflows[0];
                    let cosyflowData = null;
                    let basePrompt = '';

                    if (cosyflow.cosyflow) {
                        try {
                            cosyflowData = typeof cosyflow.cosyflow === 'string'
                                ? JSON.parse(cosyflow.cosyflow)
                                : cosyflow.cosyflow;

                            // Extract base prompt from variables
                            if (cosyflowData?.variables) {
                                for (const [key, fieldDef] of Object.entries(cosyflowData.variables)) {
                                    if (fieldDef.type === 'prompt' && fieldDef.default) {
                                        basePrompt = fieldDef.default;
                                        break;
                                    }
                                }
                            }
                        } catch (e) {
                            console.error('[TemplateDetail] Error parsing cosyflow JSON:', e);
                        }
                    }

                    // Extract example images
                    let exampleImages = [];
                    if (cosyflowData?.metadata?.example_urls) {
                        exampleImages = cosyflowData.metadata.example_urls;
                    } else if (cosyflowData?.metadata?.preview_images) {
                        exampleImages = cosyflowData.metadata.preview_images;
                    }

                    transformedTemplate = {
                        id: cosyflow.uid,
                        uid: cosyflow.uid,
                        title: cosyflow.title,
                        description: cosyflow.description || '',
                        model: cosyflow.title,
                        prompt: cosyflow.base_prompt || '',
                        basePrompt: basePrompt || cosyflow.base_prompt || '',
                        category: cosyflow.category || 'General',
                        imageUrl: cosyflow.image_url || cosyflowData?.image_url || '',
                        credits_cost: cosyflow.credits_cost || cosyflowData?.credits_cost,
                        outputType: detectOutputType(cosyflow.category),
                        creator: cosyflow.alias,
                        accountNo: cosyflow.account_no,
                        trusted: cosyflow.trusted === 1 || cosyflow.trusted === "1" || cosyflow.trusted === true,
                        isPublic: cosyflow.public === 1 || cosyflow.public === "1" || cosyflow.public === true,
                        distributed: cosyflow.distributed === 1 || cosyflow.distributed === "1" || cosyflow.distributed === true,
                        exampleImages: exampleImages,
                        cosyflowData: cosyflowData,
                        creditsPerUnit: cosyflow.creditsPerUnit || cosyflow.creditsperunit || null,
                    };
                }

                // // If cosyflow fetch failed, try template API
                // if (!transformedTemplate) {
                //     console.log('[TemplateDetail] Fetching as template:', templateId);
                //     const response = await axios.get(`${apiBase}/api/templates/${templateId}`, {
                //         headers: {
                //             "Content-Type": "application/json",
                //             "Authorization": `Bearer ${process.env.REACT_APP_API_TOKEN}`
                //         }
                //     });

                //     if (response.data) {
                //         const templateData = response.data;
                //         transformedTemplate = {
                //             id: templateData.id,
                //             title: templateData.title,
                //             description: templateData.description || '',
                //             model: templateData.model || templateData.cosyflow,
                //             prompt: templateData.base_prompt || '',
                //             basePrompt: templateData.base_prompt || '',
                //             category: templateData.category || 'General',
                //             imageUrl: templateData.image_url || '',
                //             credits_cost: templateData.credits_cost,
                //             outputType: detectOutputType(templateData.category),
                //             creator: templateData.alias || templateData.creator,
                //             exampleImages: templateData.example_images || [],
                //         };
                //     }
                // }

                if (transformedTemplate) {
                    setTemplate(transformedTemplate);

                    // Check for pending generation from login redirect
                    const pendingGen = sessionStorage.getItem('pendingGeneration');
                    if (pendingGen) {
                        try {
                            const { templateId: pendingId, userPrompt: pendingPrompt, fieldValues: pendingFieldValues } = JSON.parse(pendingGen);
                            if (pendingId === templateId) {
                                setUserPrompt(pendingPrompt || '');
                                setFieldValues(pendingFieldValues || {});
                                sessionStorage.removeItem('pendingGeneration');
                            }
                        } catch (e) {
                            console.error('[TemplateDetail] Error parsing pending generation:', e);
                        }
                    }
                } else {
                    setNotFound(true);
                }
            } catch (error) {
                console.error('[TemplateDetail] Error fetching template:', error);
                setNotFound(true);
            } finally {
                setIsLoading(false);
            }
        };

        fetchData();
    }, [templateId, passedCosyflowData]);

    // Check model availability (online + offline) when template loads
    // Also fetches the full model lists for CosyflowStatusBar when not passed via navigation
    useEffect(() => {
        if (!template?.model) return;

        const checkAvailability = async () => {
            const [onlineModels, localModels] = await Promise.all([
                getOnlineAvailableModels(),
                getLocalAvailableModels()
            ]);

            const online = onlineModels.includes(template.model);
            const offline = localModels.includes(template.model);

            setModelAvailability({ online, offline, available: online || offline, checked: true });
            setFetchedOnlineModels(onlineModels);
            setFetchedLocalModels(localModels);
            console.log(`[TemplateDetail] Model availability for "${template.model}":`, { online, offline });
        };
        checkAvailability();
    }, [template?.model]);

    // Fetch model defaults when template loads
    useEffect(() => {
        if (!template?.model) return;

        const fetchDefaults = async () => {
            setIsLoadingDefaults(true);
            try {
                const apiBase = await getApiBaseUrl();
                const response = await axios.get(`${apiBase}/api/cosyflows/query?title=${encodeURIComponent(template.model)}`, {
                    headers: {
                        "Content-Type": "application/json",
                        "Authorization": `Bearer ${process.env.REACT_APP_API_TOKEN}`
                    }
                });

                if (response.data?.cosyflows && response.data.cosyflows.length > 0) {
                    const cosyflow = response.data.cosyflows[0];
                    if (cosyflow.cosyflow) {
                        const cosyflowData = typeof cosyflow.cosyflow === 'string'
                            ? JSON.parse(cosyflow.cosyflow)
                            : cosyflow.cosyflow;

                        const defaults = {};
                        const importance5Fields = [];

                        if (cosyflowData.variables) {
                            for (const [key, obj] of Object.entries(cosyflowData.variables)) {
                                if (obj && typeof obj === "object") {
                                    if ("default" in obj) {
                                        defaults[key] = obj.default;
                                    }
                                    if (obj.importance === 5) {
                                        importance5Fields.push({
                                            key,
                                            type: obj.type || 'string',
                                            label: obj.label || obj.description || key,
                                            description: obj.description || '',
                                            default: obj.default,
                                            options: obj.options || null,
                                            min: obj.min,
                                            max: obj.max,
                                            step: obj.step,
                                        });
                                    }
                                }
                            }
                        }

                        setModelDefaults({
                            defaults,
                            schema: cosyflowData.variables
                        });
                        setRequiredFields(importance5Fields);

                        const initialValues = {};
                        importance5Fields.forEach(field => {
                            if (field.default !== undefined) {
                                if (isFileType(field.type)) {
                                    const defaultVal = field.default;
                                    const isValidUrl = typeof defaultVal === 'string' &&
                                        (defaultVal.startsWith('http://') || defaultVal.startsWith('https://'));
                                    const isValidBase64 = typeof defaultVal === 'string' &&
                                        defaultVal.startsWith('data:');

                                    if (isValidUrl || isValidBase64) {
                                        initialValues[field.key] = field.default;
                                    }
                                } else {
                                    initialValues[field.key] = field.default;
                                }
                            }
                        });
                        setFieldValues(initialValues);
                    }
                }
            } catch (error) {
                console.error('[TemplateDetail] Error fetching model defaults:', error);
            } finally {
                setIsLoadingDefaults(false);
            }
        };

        fetchDefaults();
    }, [template?.model]);

    const handleFieldChange = (fieldName, value) => {
        setFieldValues((prev) => ({ ...prev, [fieldName]: value }));
    };

    const handleMediaUpload = (fieldName, e) => {
        const file = e.target.files?.[0];
        if (file) {
            const reader = new FileReader();
            reader.onloadend = () => {
                const base64 = reader.result;
                setFieldValues((prev) => ({ ...prev, [fieldName]: base64 }));
            };
            reader.readAsDataURL(file);
        }
    };

    const handleRemoveMedia = (fieldName) => {
        setFieldValues((prev) => {
            const updated = { ...prev };
            delete updated[fieldName];
            return updated;
        });
        if (fileInputRefs.current[fieldName]) {
            fileInputRefs.current[fieldName].value = '';
        }
    };

    const isFileType = (type) => {
        if (!type) return false;
        const fileTypes = ['IMAGE', 'b64data', 'file', 'b64', 'image', 'VIDEO', 'video', 'AUDIO', 'audio'];
        const lower = type.toLowerCase();
        return fileTypes.some(ft => ft.toLowerCase() === lower) ||
               lower.includes('image') || lower.includes('video') || lower.includes('audio') ||
               lower.includes('b64') || lower.includes('file');
    };

    const getMediaTypeFromBase64 = (base64String) => {
        if (!base64String || typeof base64String !== 'string') return 'unknown';
        if (base64String.startsWith('data:image/')) return 'image';
        if (base64String.startsWith('data:video/')) return 'video';
        if (base64String.startsWith('data:audio/')) return 'audio';
        return 'unknown';
    };

    const isValidFileValue = (value) => {
        if (!value || typeof value !== 'string') return false;
        return value.startsWith('data:') ||
               value.startsWith('http://') ||
               value.startsWith('https://');
    };



    const handleGenerate = useCallback(async () => {
        if (!template) return;

        if (requiredFields.length === 0) {
            if (!userPrompt.trim()) {
                Swal.fire({
                    title: 'Prompt Required',
                    text: 'Please enter a prompt to generate content.',
                    icon: 'warning',
                    confirmButtonColor: '#FFB02E',
                });
                return;
            }
        } else {
            const emptyFields = requiredFields.filter(field => {
                const value = fieldValues[field.key];
                if (field.type === 'boolean') return false;
                if (field.type === 'number' || field.type === 'integer' || field.type === 'float') {
                    return value === undefined || value === null || value === '';
                }
                return !value || String(value).trim().length === 0;
            });

            if (emptyFields.length > 0) {
                Swal.fire({
                    title: 'Required Fields',
                    text: `Please fill in all required fields: ${emptyFields.map(f => f.label).join(', ')}`,
                    icon: 'warning',
                    confirmButtonColor: '#FFB02E',
                });
                return;
            }
        }

        if (dynamicFormRef.current?.validateRequiredFields) {
            const validation = dynamicFormRef.current.validateRequiredFields();
            if (!validation.valid) {
                const fieldNames = validation.errors.map(e => e.label).join(', ');
                Swal.fire({
                    title: 'Required Fields Missing',
                    text: `Please fill in the following required fields: ${fieldNames}`,
                    icon: 'warning',
                    confirmButtonColor: '#FFB02E',
                });
                return;
            }
        }

        const accountNo = accountContext.accountDetails?.accountNo;
        if (!accountNo) {
            setShowLoginModal(true);
            return;
        }

        setIsGenerating(true);
        setGenerationProgress(0);
        setGenerationStatus('Starting generation...');

        try {
            const setNestedProperty = (obj, path, value) => {
                const keys = path.split('.');
                const lastKey = keys.pop();
                const target = keys.reduce((acc, key) => {
                    if (!acc[key]) acc[key] = {};
                    return acc[key];
                }, obj);
                target[lastKey] = value;
            };

            const params = {};

            // Apply defaults
            if (modelDefaults?.defaults) {
                for (const [key, value] of Object.entries(modelDefaults.defaults)) {
                    if (key.includes('.')) {
                        setNestedProperty(params, key, value);
                    } else {
                        params[key] = value;
                    }
                }
            }

            // Build final prompt
            const basePromptText = template.basePrompt || template.prompt;
            const finalPrompt = basePromptText ? `${basePromptText}, ${userPrompt}` : userPrompt;

            // Find and set prompt field
            if (modelDefaults?.schema) {
                for (const [key, fieldDef] of Object.entries(modelDefaults.schema)) {
                    if (fieldDef && fieldDef.type === 'prompt') {
                        if (key.includes('.')) {
                            setNestedProperty(params, key, finalPrompt);
                        } else {
                            params[key] = finalPrompt;
                        }
                        break;
                    }
                }
            }

            // Set required field values (importance 5 fields)
            if (requiredFields.length > 0) {
                for (const field of requiredFields) {
                    const value = fieldValues[field.key];
                    if (value !== undefined && value !== null && value !== '') {
                        let processedValue = value;
                        if (isFileType(field.type) && typeof value === 'string' && value.includes('base64,')) {
                            processedValue = value.split('base64,')[1];
                        }
                        if (field.key.includes('.')) {
                            setNestedProperty(params, field.key, processedValue);
                        } else {
                            params[field.key] = processedValue;
                        }
                    }
                }
            }

            console.log('[TemplateDetails] params: ', params);
            console.log('[TemplateDetails] template: ', template);

            if (!template.model || template.model.trim() === '') {
                console.error('[TemplateDetail] No model/cosyflow found for this template');
                Swal.fire({
                    title: 'Model Missing',
                    text: 'This template does not have a valid model assigned. Please try a different template.',
                    icon: 'error',
                    confirmButtonColor: '#FFB02E',
                });
                return;
            }

            const templateSrc = electronBridge.isElectron ? 'PWA' : 'PWeb' + '_Showcase'

            const promptData = {
                prompt: {
                    ...params,
                    count: 1,
                    cosyflow: template.model
                },
                accountNo,
                source: templateSrc,
                version: process.env.REACT_APP_VERSION
            };
            console.log('[TemplateDetails] promptData: ', promptData);

            try {
                const response = await fetch('https://api.ipify.org');
                promptData.ip_address = await response.text();
            } catch (e) {
                promptData.ip_address = '';
            }

            setGenerationProgress(10);

            const attemptGeneration = async (targetServerUrl = null, isOfflineFallback = false) => {
                let new_promptData = promptData

                if (isOfflineFallback) {
                    new_promptData = promptData.prompt
                }
                
                const data = await generatePrompt(new_promptData, apiState, generateStatusDispatch, targetServerUrl);
                console.log('[MODEL FALLBACK] Data from generatePrompt call: ', data);

                if (data.error) {
                    const errorMessage = typeof data.error === 'string' ? data.error : JSON.stringify(data.error);

                    if (errorMessage === "Model unavailable" && !isOfflineFallback) {
                        console.log('[MODEL FALLBACK] Model unavailable from online API, checking local servers...');
                        setGenerationStatus('Checking local servers...');

                        const { available, servers } = await checkOfflineServersAvailable();

                        if (available) {
                            const modelName = template.model;
                            const serverInfo = await getServerForModel(modelName);

                            if (serverInfo && serverInfo.serverUrl) {
                                console.log(`[MODEL FALLBACK] Found model "${modelName}" on local ${serverInfo.serverType} server: ${serverInfo.serverUrl}`);
                                setGenerationStatus(`Using local ${serverInfo.serverType.toUpperCase()} server...`);
                                return attemptGeneration(serverInfo.serverUrl, true);
                            } else {
                                console.log(`[MODEL FALLBACK] Model "${modelName}" not found on any local server`);
                                Swal.fire({
                                    title: 'Model Not Available',
                                    text: `The model "${modelName}" is not available online and was not found on any local server. Please install the model locally or try a different template.`,
                                    icon: 'warning',
                                    confirmButtonColor: '#FFB02E',
                                });
                                return null;
                            }
                        } else {
                            console.log('[MODEL FALLBACK] No local servers available');
                            Swal.fire({
                                title: 'Model Not Available',
                                text: `The model "${template.model}" is not available online and no local servers are running. Please start your local GPU or Cosy server, or try a different template.`,
                                icon: 'warning',
                                confirmButtonColor: '#FFB02E',
                            });
                            return null;
                        }
                    }

                    throw new Error(data.error);
                }

                return { data, serverUrl: targetServerUrl };
            };

            const result = await attemptGeneration();

            if (!result || !result.data) {
                return;
            }

            const { data, serverUrl: usedServerUrl } = result;

            const jobId = data.uid || data.prompt_id;
            if (!jobId) throw new Error('No job ID returned');

            setGenerationProgress(10);
            setGenerationStatus('Generating your content...');

            const isCosyActive = jobId.includes("-");

            const getMediaTypeFromUrl = (url) => {
                if (!url) return 'image';
                const lower = url.toLowerCase();
                if (lower.includes('.mp4') || lower.includes('.webm') || lower.includes('.mov')) return 'video';
                if (lower.includes('.mp3') || lower.includes('.wav') || lower.includes('.ogg') || lower.includes('.flac')) return 'audio';
                if (lower.includes('.glb') || lower.includes('.gltf') || lower.includes('.obj')) return '3d';
                return 'image';
            };

            const TIMEOUT_DURATION = 90000; // 90 seconds
            let lastProgressTime = Date.now();
            let lastProgress = -1;

            while (true) {
                await new Promise(resolve => setTimeout(resolve, 2000));

                // Check for timeout (no progress for 90 seconds)
                const timeSinceLastProgress = Date.now() - lastProgressTime;
                if (timeSinceLastProgress > TIMEOUT_DURATION) {
                    console.error(`[TemplateDetail] Generation timed out - no progress for ${Math.round(timeSinceLastProgress / 1000)} seconds`);
                    throw new Error('Generation timed out - no progress for 90 seconds');
                }

                try {
                    const progressData = await getProgress(jobId, isCosyActive, apiState, generateStatusDispatch, usedServerUrl);
                    console.log('[TemplateDetail] Progress data:', progressData);

                    const progress = progressData.percent || progressData.progress || 0;
                    if (progress > 0) {
                        const displayProgress = progress;
                        setGenerationProgress(displayProgress);
                        setGenerationStatus(`Generating... ${Math.round(displayProgress)}%`);

                        // Reset timeout if progress changed
                        if (progress !== lastProgress) {
                            console.log(`[TemplateDetail] Progress updated: ${lastProgress}% -> ${progress}%, resetting timeout`);
                            lastProgressTime = Date.now();
                            lastProgress = progress;
                        }
                    }

                    if (progressData.error && progressData.error !== "None") {
                        throw new Error(progressData.error);
                    }

                    if ((progressData.output && Array.isArray(progressData.output) && progressData.output.length > 0) || (progressData.outputs && Array.isArray(progressData.outputs) && progressData.outputs.length > 0)) {
                        // const outputItem = progressData.output[0];
                        let outputUid = null;

                        if (usedServerUrl && progressData.outputs?.length > 0 && progressData.outputs[0]?.view?.filename) {
                            outputUid = progressData.outputs[0].view.filename; 
                        } else if (progressData.output?.length > 0 && progressData.output[0]?.uid) {
                            outputUid = progressData.output[0].uid;
                        }

                        if (outputUid) {
                            console.log('[TemplateDetail] Fetching output for:', outputUid);

                            const getGenerateOutputWithRetry = async (maxRetries = 5, delayMs = 1000) => {
                                for (let attempt = 1; attempt <= maxRetries; attempt++) {
                                    try {
                                        console.log(`[TemplateDetail] Attempting to get generate output (attempt ${attempt}/${maxRetries})`);
                                        let result;
                                        if (usedServerUrl) {
                                            result = await getComfyOutput(jobId, outputUid, apiState, generateStatusDispatch, usedServerUrl);
                                        } else {
                                            result = await getGenerateOutput(jobId, outputUid);
                                        }

                                        if (result && result.url && result.url.trim() !== '') {
                                            console.log(`[TemplateDetail] URL found on attempt ${attempt}: ${result.url}`);
                                            return result;
                                        }

                                        if (result && result.image) {
                                            console.log(`[TemplateDetail] Base64 data found on attempt ${attempt}, URL not ready yet`);
                                            if (attempt === maxRetries) {
                                                return result;
                                            }
                                        }
                                    } catch (err) {
                                        console.log(`[TemplateDetail] Retry ${attempt} error:`, err.message);
                                    }

                                    if (attempt < maxRetries) {
                                        await new Promise(resolve => setTimeout(resolve, delayMs));
                                    }
                                }
                                return null;
                            };

                            const outputData = await getGenerateOutputWithRetry();

                            if (outputData) {
                                setGenerationProgress(100);
                                console.log('[TemplateDetail] outputData:', outputData);

                                const isUrl = (str) => {
                                    if (!str) return false;
                                    const trimmed = str.trim().toLowerCase();
                                    return trimmed.startsWith('http://') || trimmed.startsWith('https://') || trimmed.startsWith('data:');
                                };

                                let mediaType = outputData.url ? getMediaTypeFromUrl(outputData.url) : 'Unknown';
                                if ((mediaType === 'Unknown' || mediaType === 'image') && outputData.image) {
                                    console.log("[TemplateDetail] Using base64 to detect media type");
                                    const detectedType = await getMediaType(outputData.image);
                                    if (detectedType && detectedType !== 'Unknown Media Type' && detectedType !== 'Invalid Base64 String') {
                                        mediaType = detectedType.toLowerCase();
                                    }
                                }
                                console.log('[TemplateDetail] Detected media type:', mediaType);

                                let imageData;

                                if (outputData.url && outputData.url.trim() !== '') {
                                    imageData = outputData.url;
                                } else if (outputData.image) {
                                    if (isUrl(outputData.image)) {
                                        imageData = outputData.image;
                                    } else {
                                        const prefix = mediaType === 'video' ? 'data:video/mp4;base64,' :
                                                       mediaType === 'audio' ? `data:${getAudioMimeFromBase64(outputData.image)};base64,` :
                                                       'data:image/webp;base64,';
                                        imageData = `${prefix}${outputData.image}`;
                                    }
                                }

                                console.log('[TemplateDetail] Final imageData:', imageData?.substring(0, 100));

                                const saveResult = await saveGeneratedMedia({
                                    imageData: imageData,
                                    base64Data: outputData.image,
                                    jobUid: jobId,
                                    outputUid: outputUid,
                                    seed: outputData.seed || null,
                                    model: template.model || '',
                                    prompt: userPrompt || '',
                                    tag: template.category || '',
                                    isOnline: !usedServerUrl,
                                    serverUrl: outputData.url || null,
                                    dataType: mediaType,
                                });

                                console.log('[TemplateDetail] Media saved to DB:', saveResult);

                                let thumbnail = null;
                                if (mediaType === 'video' && imageData) {
                                    thumbnail = await generateVideoThumbnail(imageData);
                                }

                                const newGeneration = {
                                    image: imageData,
                                    thumbnail: thumbnail,
                                    serverUrl: outputData.url || null,
                                    type: mediaType,
                                    timestamp: Date.now(),
                                    dbId: saveResult.id,
                                    hash: saveResult.hash
                                };
                                setSessionGenerations(prev => [newGeneration, ...prev]);
                                setGeneratedImage(newGeneration);
                                setShowGeneratedModal(true);
                                return;
                            }
                        }
                    }
                } catch (err) {
                    console.error('[TemplateDetail] Error polling progress:', err);
                    if (err.message && !err.message.includes('timeout')) {
                        throw err;
                    }
                }
            }
        } catch (error) {
            console.error('[TemplateDetail] Generation error:', error);

            const errorMsg = error.message || '';
            if (errorMsg.includes('Out of Credits') || errorMsg.includes('Insufficient Credits')) {
                Swal.fire({
                    title: 'Insufficient Credits',
                    html: 'You are out of credits. Please top up to continue generating content.',
                    icon: 'error',
                    confirmButtonColor: '#FFB02E',
                });
            } else {
                Swal.fire({
                    title: 'Generation Failed',
                    text: errorMsg || 'Failed to generate content. Please try again.',
                    icon: 'error',
                    confirmButtonColor: '#FFB02E',
                });
            }
        } finally {
            setIsGenerating(false);
            setGenerationProgress(0);
            setGenerationStatus('');
        }
    }, [accountContext.accountDetails, template, userPrompt, fieldValues, modelDefaults, requiredFields, apiState, generateStatusDispatch, navigate]);

    const variableCostProduct = useMemo(() => {
        return computeVariableCostProduct(modelDefaults?.schema, fieldValues);
    }, [modelDefaults?.schema, fieldValues]);

    if (isLoading) {
        return <PageLoading text="Loading template..." />;
    }

    if (notFound || !template) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <div className="text-center">
                    <h1 className="text-2xl font-bold text-white mb-4">Template Not Found</h1>
                    <p className="text-white/60 mb-6">The template you're looking for doesn't exist.</p>
                    <button
                        onClick={() => navigate('/cosyflows')}
                        className="bg-yellow hover:bg-yellow/90 text-dark-blue font-bold py-2 px-4 rounded-lg transition-all"
                    >
                        Back to Templates
                    </button>
                </div>
            </div>
        );
    }

    const outputType = template.outputType || 'image';
    const typeConfig = outputTypeConfig[outputType] || outputTypeConfig.image;
    const hasValidImage = template.imageUrl && !template.imageUrl.includes('placeholder.png');
    const baseCost = getCostDisplay(template.category)
        ? parseInt(getCostDisplay(template.category), 10)
        : (template.credits_cost ? Number(template.credits_cost) : null);
    const cpu = template.creditsPerUnit ? parseFloat(template.creditsPerUnit) : 0;
    const displayCost = baseCost !== null
        ? String(Math.round(baseCost + (variableCostProduct * cpu) + 6))
        : null;
    const promptText = template.basePrompt || template.prompt;

    return (
        <div className="min-h-screen w-full bg-dark-blue">
            {/* Full-width Header with Back Button */}
            <div className="w-full bg-gradient-to-b from-dark-blue/80 to-transparent sticky top-0 z-10 backdrop-blur-sm">
                <div className="w-full px-6 lg:px-12 py-4">
                    <button
                        onClick={() => navigate('/cosyflows')}
                        className="text-white/70 hover:text-yellow transition-colors flex items-center gap-3 text-lg font-medium group"
                    >
                        <svg className="w-5 h-5 group-hover:-translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                        </svg>
                        Back to Templates
                    </button>
                </div>
            </div>

            {/* Main Content - Full Width */}
            <div className="w-full px-6 lg:px-12 pb-12">
                <div className="grid grid-cols-1 xl:grid-cols-5 gap-8 lg:gap-12">
                    {/* Left Column - Template Preview (3/5 width on xl) */}
                    <div className="xl:col-span-3 space-y-8">
                        {/* Template Media - Large */}
                        <div className="bg-gradient-to-br from-white/5 to-white/[0.02] border border-white/10 rounded-2xl overflow-hidden shadow-2xl max-h-[50vh]">
                            {template.exampleImages && template.exampleImages.length > 0 ? (
                                <div className="p-2 h-full overflow-hidden">
                                    <MediaSlider
                                        images={template.exampleImages}
                                        alt={template.title}
                                    />
                                </div>
                            ) : (
                                <div className="relative aspect-[16/10] overflow-hidden">
                                    {!hasValidImage && outputType === 'video' ? (
                                        <div className={`absolute inset-0 ${mediaBackgroundColors.video} flex flex-col items-center justify-center`}>
                                            <div className="w-24 h-24 rounded-full bg-purple-500/30 flex items-center justify-center mb-4">
                                                <svg className="w-12 h-12 text-purple-400" fill="currentColor" viewBox="0 0 24 24">
                                                    <path d="M8 5v14l11-7z" />
                                                </svg>
                                            </div>
                                            <span className="text-purple-300 text-xl font-medium">Video Template</span>
                                        </div>
                                    ) : !hasValidImage && outputType === 'audio' ? (
                                        <div className={`absolute inset-0 ${mediaBackgroundColors.audio} flex flex-col items-center justify-center`}>
                                            <div className="w-24 h-24 rounded-full bg-green-500/30 flex items-center justify-center mb-4">
                                                <svg className="w-12 h-12 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2z" />
                                                </svg>
                                            </div>
                                            <span className="text-green-300 text-xl font-medium">Audio Template</span>
                                        </div>
                                    ) : !hasValidImage && outputType === '3d' ? (
                                        <div className={`absolute inset-0 ${mediaBackgroundColors['3d']} flex flex-col items-center justify-center`}>
                                            <div className="w-24 h-24 rounded-full bg-orange-500/30 flex items-center justify-center mb-4">
                                                <svg className="w-12 h-12 text-orange-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 10l-2 1m0 0l-2-1m2 1v2.5M20 7l-2 1m2-1l-2-1m2 1v2.5M14 4l-2-1-2 1M4 7l2-1M4 7l2 1M4 7v2.5M12 21l-2-1m2 1l2-1m-2 1v-2.5M6 18l-2-1v-2.5M18 18l2-1v-2.5" />
                                                </svg>
                                            </div>
                                            <span className="text-orange-300 text-xl font-medium">3D Template</span>
                                        </div>
                                    ) : (
                                        <img
                                            src={hasValidImage ? template.imageUrl : getPlaceholderImage(template.id)}
                                            alt={template.title}
                                            className="w-full h-full object-cover"
                                        />
                                    )}
                                </div>
                            )}
                        </div>

                        {/* Template Info - Large Card */}
                        <div className="bg-gradient-to-br from-white/5 to-white/[0.02] border border-white/10 rounded-2xl p-8 space-y-6">
                            {/* Header Row */}
                            <div className="flex flex-wrap items-center justify-between gap-4">
                                <div className="flex items-center gap-3">
                                    <span className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold ${typeConfig.color}`}>
                                        {typeConfig.icon}
                                        {template.category}
                                    </span>
                                    {template.trusted && (
                                        <span className="inline-flex items-center gap-1 px-3 py-2 rounded-full text-sm font-medium bg-emerald-500/20 text-emerald-400">
                                            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                                                <path d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                                            </svg>
                                            Verified
                                        </span>
                                    )}
                                </div>
                                <div className="flex items-center gap-3">
                                    {/* YouTube Tutorials Button */}
                                    {tutorialVideos.length > 0 && (
                                        <div className="relative inline-block" ref={tutorialDropdownRef}>
                                            <button
                                                onClick={() => setShowTutorialDropdown(!showTutorialDropdown)}
                                                className="py-2 px-4 border rounded bg-red-600 hover:bg-red-700 transition text-white text-sm flex items-center gap-2"
                                            >
                                                <SiYoutube size={20} />
                                                <span className="hidden sm:inline">Tutorials</span>
                                            </button>

                                            {showTutorialDropdown && (
                                                <div className="absolute right-0 top-full mt-2 w-80 bg-[#1a1a2e] border border-[#2a2a4a] rounded-lg shadow-xl overflow-hidden z-[9999]">
                                                    <div className="p-2 border-b border-[#2a2a4a]">
                                                        <p className="text-xs text-[#a0a0b0] font-medium px-1">Related Tutorials</p>
                                                    </div>
                                                    <div className="max-h-96 overflow-y-auto">
                                                        {tutorialVideos.map(video => (
                                                            <button
                                                                key={video.id}
                                                                onClick={() => {
                                                                    setSelectedTutorialVideo(video);
                                                                    setShowTutorialDropdown(false);
                                                                }}
                                                                className="w-full flex items-center gap-3 p-2.5 hover:bg-white/5 transition-colors text-left"
                                                            >
                                                                <div className="w-24 h-14 rounded overflow-hidden flex-shrink-0 bg-black/20">
                                                                    <img
                                                                        src={getThumbnailUrl(video.id, 'mqdefault')}
                                                                        alt={video.title}
                                                                        className="w-full h-full object-cover"
                                                                        loading="lazy"
                                                                    />
                                                                </div>
                                                                <span className="text-white text-sm line-clamp-2 leading-snug flex-1">
                                                                    {video.title}
                                                                </span>
                                                            </button>
                                                        ))}
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    )}

                                    <div className="flex items-center gap-3 bg-yellow/10 px-5 py-2.5 rounded-full">
                                        <svg className="w-6 h-6 text-yellow" viewBox="0 0 24 24" fill="currentColor">
                                            <circle cx="12" cy="12" r="10" fill="currentColor" opacity="0.3" />
                                            <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8zm-1-13h2v6h-2zm0 8h2v2h-2z" fill="currentColor" />
                                        </svg>
                                        <span className="text-xl font-bold text-yellow">
                                            {displayCost ? `${displayCost} credits` : 'Free'}
                                        </span>
                                    </div>
                                </div>
                            </div>

                            {/* Title */}
                            <h1 className="text-3xl lg:text-4xl font-bold text-white leading-tight">{template.title}</h1>

                            {/* Description */}
                            <p className="text-lg text-white/70 leading-relaxed">{template.description || 'No description available'}</p>

                            {/* Model Info */}
                            {isLoadingDefaults ? (
                                <div className="flex items-center gap-3 text-white/50">
                                    <ButtonSpinner />
                                    <span className="text-base">Loading model settings...</span>
                                </div>
                            ) : (
                                <div className="flex items-center gap-3 bg-white/5 rounded-xl px-5 py-3">
                                    <span className="text-base text-white/50">Model:</span>
                                    <span className="text-base text-yellow font-semibold">{template.model}</span>
                                </div>
                            )}

                            {/* Creator */}
                            {template.creator && (
                                <div className="flex items-center gap-3 pt-4 border-t border-white/10">
                                    <div className="w-10 h-10 rounded-full bg-yellow/20 flex items-center justify-center">
                                        <svg className="w-5 h-5 text-yellow" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                                        </svg>
                                    </div>
                                    <span className="text-base text-white/60">Created by <span className="text-white font-medium">{template.creator}</span></span>
                                </div>
                            )}
                        </div>

                        {/* Base Prompt - Collapsible */}
                        {promptText && (
                            <div className="bg-gradient-to-br from-yellow/10 to-yellow/5 border border-yellow/20 rounded-2xl p-8">
                                <div className="flex items-center justify-between mb-4">
                                    <h3 className="text-lg font-semibold text-white/80">Base Prompt</h3>
                                    {promptText.length > PROMPT_CHAR_LIMIT && (
                                        <button
                                            onClick={() => setIsPromptExpanded(!isPromptExpanded)}
                                            className="text-sm text-yellow hover:text-yellow/80 transition-colors flex items-center gap-2 font-medium"
                                        >
                                            {isPromptExpanded ? (
                                                <>
                                                    Show less
                                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                                                    </svg>
                                                </>
                                            ) : (
                                                <>
                                                    Show more
                                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                                    </svg>
                                                </>
                                            )}
                                        </button>
                                    )}
                                </div>
                                <p className="text-base text-white/90 whitespace-pre-wrap break-words leading-relaxed">
                                    {promptText.length <= PROMPT_CHAR_LIMIT
                                        ? promptText
                                        : isPromptExpanded
                                            ? promptText
                                            : `${promptText.slice(0, PROMPT_CHAR_LIMIT)}...`}
                                </p>
                            </div>
                        )}
                    </div>

                    {/* Right Column - Generation Form (2/5 width on xl) */}
                    <div className="xl:col-span-2">
                        {/* Sticky Form Container */}
                        <div className="xl:sticky space-y-6">
                            {/* Cosyflow Status Bar */}
                            {template && (
                                <CosyflowStatusBar
                                    size="large"
                                    item={passedCosyflowData || {
                                        uid: template.uid,
                                        title: template.model || template.title,
                                        description: template.description,
                                        category: template.category,
                                        isPublic: template.isPublic,
                                        distributed: template.distributed,
                                        trusted: template.trusted,
                                        hasComfyWorkflow: !!template.cosyflowData?.workflow,
                                        cosyflow: template.cosyflowData,
                                        isLocal: false,
                                        accountNo: template.accountNo,
                                    }}
                                    isOwned={passedCosyflowData ? (accountContext?.accountDetails?.accountNo === passedCosyflowData.accountNo) : (accountContext?.accountDetails?.accountNo === template.accountNo)}
                                    availableModels={passedAvailableModels?.length > 0 ? passedAvailableModels : fetchedOnlineModels}
                                    localAvailableModels={passedLocalAvailableModels?.length > 0 ? passedLocalAvailableModels : fetchedLocalModels}
                                    offlineModels={[]}
                                    isStaffAccount={isStaff(accountContext?.accountDetails)}
                                    isStaffOrCreatorAccount={isStaffOrCreator(accountContext?.accountDetails)}
                                    isInstalling={checkIsInstalling(passedCosyflowData?.uid || template?.uid)}
                                    onRunInPlayground={cosyflowHandlers.handleRunInPlayground}
                                    onRunOfflineInPlayground={cosyflowHandlers.handleRunOfflineInPlayground}
                                    onRunInCosyUI={cosyflowHandlers.handleRunInCosyUI}
                                    onRunInComfyUI={cosyflowHandlers.handleRunInComfyUI}
                                    onInstallLocally={cosyflowHandlers.handleInstallLocally}
                                    onModifyCosyflow={cosyflowHandlers.handleModifyCosyflow}
                                    onCreateFromDistributed={cosyflowHandlers.handleCreateFromDistributed}
                                    onDeleteCosyflow={cosyflowHandlers.handleDeleteCosyflow}
                                    onCreateTemplate={cosyflowHandlers.handleCreateTemplate}
                                    onTogglePublic={cosyflowHandlers.handleTogglePublic}
                                    onToggleDistributed={cosyflowHandlers.handleToggleDistributed}
                                />
                            )}
                            <div className="bg-gradient-to-br from-white/5 to-white/[0.02] border border-white/10 rounded-2xl p-8 space-y-8">
                                <h2 className="text-2xl font-bold text-white">Generate Content</h2>

                                {/* User Prompt Input - Only show if no importance 5 fields */}
                                {requiredFields.length === 0 && (
                                    <div>
                                        <label className="block text-base font-semibold text-white mb-3">
                                            Your Prompt <span className="text-red-400">*</span>
                                        </label>
                                        <textarea
                                            value={userPrompt}
                                            onChange={(e) => setUserPrompt(e.target.value)}
                                            placeholder="Describe what you want to generate..."
                                            className="w-full h-40 resize-none bg-white/5 border border-white/20 rounded-xl px-5 py-4 text-lg text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-yellow/50 focus:border-yellow transition-all"
                                            disabled={isGenerating}
                                        />
                                        <p className="text-sm text-white/50 mt-3">
                                            Your prompt will be combined with the template's base prompt.
                                        </p>
                                    </div>
                                )}

                                {/* Required Fields (Importance 5) - Using DynamicPromptForm */}
                                {requiredFields.length > 0 && modelDefaults?.schema && (
                                    <DynamicPromptForm
                                        ref={dynamicFormRef}
                                        schema={modelDefaults.schema}
                                        initialValues={fieldValues}
                                        importanceFilter={5}
                                        isComfyUIModel={false}
                                        selectedModel={template?.model}
                                        onFieldChange={handleFieldChange}
                                        onB64Upload={(key, value) => handleFieldChange(key, value)}
                                    />
                                )}

                                {/* Generation Progress */}
                                {isGenerating && (
                                    <div className="bg-white/5 rounded-xl p-5">
                                        <div className="flex items-center justify-between mb-3">
                                            <span className="text-base text-white/70">{generationStatus}</span>
                                            <span className="text-base font-bold text-yellow">{generationProgress}%</span>
                                        </div>
                                        <div className="h-3 bg-white/10 rounded-full overflow-hidden">
                                            <div
                                                className="h-full bg-gradient-to-r from-yellow to-yellow/80 transition-all duration-300 rounded-full"
                                                style={{ width: `${generationProgress}%` }}
                                            />
                                        </div>
                                    </div>
                                )}

                                {/* Generate Button */}
                                <button
                                    onClick={handleGenerate}
                                    className="w-full bg-gradient-to-r from-yellow to-yellow/90 hover:from-yellow/90 hover:to-yellow/80 text-dark-blue text-xl font-bold py-5 px-6 rounded-xl transition-all flex items-center justify-center gap-3 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg shadow-yellow/20 hover:shadow-yellow/30"
                                    disabled={isGenerating || (modelAvailability.checked && !modelAvailability.available)}
                                >
                                    {isGenerating ? (
                                        <>
                                            <ButtonSpinner />
                                            Generating...
                                        </>
                                    ) : (modelAvailability.checked && !modelAvailability.available) ? (
                                        <>
                                            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
                                            </svg>
                                            Model Unavailable
                                        </>
                                    ) : (
                                        <>
                                            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                                            </svg>
                                            Generate
                                        </>
                                    )}
                                </button>

                                {modelAvailability.checked && !modelAvailability.available && (
                                    <p className="text-base text-red-400 text-center">
                                        This model is not currently available online or on any local server
                                    </p>
                                )}

                                {!accountContext.accountDetails?.accountNo && modelAvailability.available && (
                                    <p className="text-base text-white/50 text-center">
                                        You'll be asked to log in when you click Generate
                                    </p>
                                )}
                            </div>

                            {/* Share Link */}
                            <div className="bg-gradient-to-br from-white/5 to-white/[0.02] border border-white/10 rounded-2xl p-6">
                                <h3 className="text-base font-semibold text-white mb-4">Share this template</h3>
                                <div className="flex gap-3">
                                    <input
                                        type="text"
                                        readOnly
                                        value={shareUrl}
                                        className="flex-1 text-base bg-white/5 border border-white/20 rounded-xl px-4 py-3 text-white/70"
                                    />
                                    <button
                                        onClick={() => {
                                            navigator.clipboard.writeText(shareUrl);
                                            Swal.fire({
                                                title: 'Link Copied!',
                                                text: 'Template link copied to clipboard',
                                                icon: 'success',
                                                timer: 1500,
                                                showConfirmButton: false,
                                            });
                                        }}
                                        className="bg-white/10 hover:bg-white/20 text-white p-3 rounded-xl transition-colors"
                                    >
                                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                                        </svg>
                                    </button>
                                </div>
                            </div>

                            {/* Session Generations */}
                            {sessionGenerations.length > 0 && (
                                <div className="bg-gradient-to-br from-white/5 to-white/[0.02] border border-white/10 rounded-2xl p-6">
                                    <h3 className="text-base font-semibold text-white mb-4">Session Generations ({sessionGenerations.length})</h3>
                                    <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-3">
                                        {sessionGenerations.map((gen, index) => {
                                            const isUrl = (str) => {
                                                if (!str) return false;
                                                const trimmed = str.trim().toLowerCase();
                                                return trimmed.startsWith('http://') || trimmed.startsWith('https://') || trimmed.startsWith('data:');
                                            };
                                            const getBase64Prefix = () => {
                                                switch (gen.type) {
                                                    case 'video': return 'data:video/mp4;base64,';
                                                    case 'audio': return `data:${getAudioMimeFromBase64(gen.image)};base64,`;
                                                    default: return 'data:image/webp;base64,';
                                                }
                                            };
                                            const thumbUrl = isUrl(gen.image) ? gen.image : `${getBase64Prefix()}${gen.image}`;

                                            return (
                                                <button
                                                    key={gen.timestamp}
                                                    onClick={() => {
                                                        setGeneratedImage(gen);
                                                        setShowGeneratedModal(true);
                                                    }}
                                                    className="relative aspect-square rounded-xl overflow-hidden border border-white/10 hover:border-cyan/50 transition-colors group"
                                                >
                                                    {gen.type === 'image' ? (
                                                        <img
                                                            src={thumbUrl}
                                                            alt={`Generation ${index + 1}`}
                                                            className="w-full h-full object-cover"
                                                        />
                                                    ) : gen.type === 'video' ? (
                                                        gen.thumbnail ? (
                                                            <div className="relative w-full h-full">
                                                                <img
                                                                    src={gen.thumbnail}
                                                                    alt={`Video ${index + 1}`}
                                                                    className="w-full h-full object-cover"
                                                                />
                                                                <div className="absolute inset-0 flex items-center justify-center">
                                                                    <div className="w-10 h-10 rounded-full bg-black/50 flex items-center justify-center">
                                                                        <svg className="w-5 h-5 text-white ml-0.5" fill="currentColor" viewBox="0 0 24 24">
                                                                            <path d="M8 5v14l11-7z" />
                                                                        </svg>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        ) : (
                                                            <div className="w-full h-full flex items-center justify-center bg-purple-900/50">
                                                                <svg className="w-8 h-8 text-purple-400" fill="currentColor" viewBox="0 0 24 24">
                                                                    <path d="M8 5v14l11-7z" />
                                                                </svg>
                                                            </div>
                                                        )
                                                    ) : gen.type === 'audio' ? (
                                                        <div className="w-full h-full flex items-center justify-center bg-green-900/50">
                                                            <svg className="w-8 h-8 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2z" />
                                                            </svg>
                                                        </div>
                                                    ) : (
                                                        <div className="w-full h-full flex items-center justify-center bg-orange-900/50">
                                                            <svg className="w-8 h-8 text-orange-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 10l-2 1m0 0l-2-1m2 1v2.5M20 7l-2 1m2-1l-2-1m2 1v2.5M14 4l-2-1-2 1M4 7l2-1M4 7l2 1M4 7v2.5M12 21l-2-1m2 1l2-1m-2 1v-2.5M6 18l-2-1v-2.5M18 18l2-1v-2.5" />
                                                            </svg>
                                                        </div>
                                                    )}
                                                    {gen.type !== 'image' && (
                                                        <div className="absolute top-1 right-1 bg-black/60 rounded px-1.5 py-0.5">
                                                            <span className="text-[10px] text-white/80 uppercase">{gen.type}</span>
                                                        </div>
                                                    )}
                                                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors" />
                                                </button>
                                            );
                                        })}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* Generated Media Modal */}
            <GeneratedMediaModal
                media={generatedImage}
                isOpen={showGeneratedModal}
                onClose={() => {
                    setShowGeneratedModal(false);
                    setGeneratedImage(null);
                }}
            />

            {/* YouTube Tutorial Video Player Modal */}
            {selectedTutorialVideo && (
                <YouTubeVideoPlayer
                    videoId={selectedTutorialVideo.id}
                    title={selectedTutorialVideo.title}
                    onClose={() => setSelectedTutorialVideo(null)}
                />
            )}

            {/* Login Modal - shown when user tries to generate without being logged in */}
            <LoginModal
                isOpen={showLoginModal}
                onClose={() => setShowLoginModal(false)}
                onLoginSuccess={() => {
                    setShowLoginModal(false);
                }}
            />
        </div>
    );
}
